import React, { useRef, useEffect, useState, useContext, createContext } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Button, Spinner } from "react-bootstrap";
import { getToken, getUserId } from '../services/authorize';
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from '../contexts/LoadingContext';
import { useParams } from "react-router-dom";
import { convertGender } from '../utils/studentUtils';
import Swal from "sweetalert2";
import "./PrintPP.css";
import "./../print.css";
import useSchoolSettings from '../hooks/useSchoolSettings';

// ——— helpers ———
const toBEDate = (d) => {
  if (!d) return '';
  // already converted by server (convertCEToBE), just format nicely
  return String(d);
};

const creditSum = (grades, type) =>
  grades.filter(g => g.sub_type === type).reduce((s, g) => s + (g.sub_credit || 0), 0);

const gradeSum = (grades, type) => {
  const valid = grades.filter(g => g.sub_type === type && g.g_grade !== null && g.g_grade !== undefined);
  if (valid.length === 0) return '';
  const total = valid.reduce((s, g) => s + (g.sub_credit || 0) * (g.g_grade || 0), 0);
  const credits = valid.reduce((s, g) => s + (g.sub_credit || 0), 0);
  return credits > 0 ? (total / credits).toFixed(2) : '';
};

const SCORE_LABELS = { 0: 'ไม่ผ่าน', 1: 'ผ่าน', 2: 'ดี', 3: 'ดีเยี่ยม' };

const gpaLabel = (gpa) => {
  const v = parseFloat(gpa);
  if (!gpa || isNaN(v)) return '';
  if (v >= 2.5) return 'ดีมาก';
  if (v >= 2.0) return 'ดี';
  return 'พอใช้';
};

const scoreToGrade = (score) => {
  const s = Number(score);
  if (score === null || score === undefined || score === '' || isNaN(s)) return null;
  if (s >= 80) return 4;
  if (s >= 75) return 3.5;
  if (s >= 70) return 3;
  if (s >= 65) return 2.5;
  if (s >= 60) return 2;
  if (s >= 55) return 1.5;
  if (s >= 50) return 1;
  return 0;
};

const desirableSummary = (semScores) => {
  if (!semScores) return '';
  const vals = [1,2,3,4,5,6,7,8].map(i => semScores[`dr_item${i}`]);
  if (vals.some(v => v == null)) return '';
  const avg = vals.reduce((s, v) => s + Number(v), 0) / 8;
  if (avg >= 2.5) return 'ดีเยี่ยม';
  if (avg >= 1.5) return 'ดี';
  if (avg >= 1) return 'ผ่าน';
  return 'ไม่ผ่าน';
};

const DESIRABLE = [
  'รักชาติ ศาสน์ กษัตริย์',
  'ซื่อสัตย์สุจริต',
  'มีวินัย',
  'ใฝ่เรียนรู้',
  'อยู่อย่างพอเพียง',
  'มุ่งมั่นในการทำงาน',
  'รักความเป็นไทย',
  'มีจิตสาธารณะ',
];

const ACTIVITY_DEFAULT = [
  { name: 'กิจกรรมแนะแนว',                    code: 'ก21101', credit: 20 },
  { name: 'กิจกรรมลูกเสือ เนตรนารี',           code: 'ก21102', credit: 15 },
  { name: 'กิจกรรมชุมนุม',                     code: 'ก21103', credit: 20 },
  { name: 'กิจกรรมเพื่อสังคมและสาธารณประโยชน์', code: 'ก21104', credit: 10 },
];

const OPINION_ITEMS = [
  'หน้าที่รับผิดชอบที่โรงเรียน',
  'ความเอาใจใส่การเรียน',
  'ความสัมพันธ์กับครูและเพื่อน',
  'อุปนิสัย – บุคลิกภาพ',
  'สุขภาพ',
  'ควรส่งเสริมในด้าน',
  'อื่น ๆ',
];

const SchoolCtx = createContext({});

// ——— print helper ———
const printNode = (node) => {
  if (!node) return;
  const w = window.open('', '_blank');
  w.document.write(`
    <html><head><title>ปพ.6</title>
    <style>
      @page { size: A4; margin: 0; }
      body { margin: 0; font-family: 'Cordia New','Tahoma',Arial,sans-serif; font-size: 14pt; }
      table { border-collapse: collapse; width: 100%; }
      td, th { border: 1px solid #000; padding: 2px 4px; vertical-align: middle; }
      .no-border td, .no-border th { border: none; }
      .a4-print { width: 210mm; padding: 12mm 14mm; box-sizing: border-box; }
      .no-print { display: none !important; }
      .print-only { display: inline !important; }
    </style>
    </head><body>
    <div class="a4-print">${node.innerHTML}</div>
    </body></html>
  `);
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); w.close(); }, 400);
};

// ——— sub-components ———

function CoverPage({ student, historyRows, section, roomStudentNumbers }) {
  // section: 'junior' (ม.1-3, c_id 1-3) or 'high' (ม.4-6, c_id 4-6)
  const ref = useRef();
  const { school_name, school_address, principal_name, school_office, logoSrc } = useContext(SchoolCtx);
  const cIds = section === 'junior' ? [1, 2, 3] : [4, 5, 6];
  const levelLabel = section === 'junior'
    ? 'ระดับมัธยมศึกษาตอนต้น (ชั้นมัธยมศึกษาปีที่ 1 – 3)'
    : 'ระดับมัธยมศึกษาตอนปลาย (ชั้นมัธยมศึกษาปีที่ 4 – 6)';

  // unique years in this section, ordered
  const years = [...new Map(
    historyRows
      .filter(r => cIds.includes(Number(r.c_id)))
      .map(r => [r.year_id, r])
  ).values()];

  const firstYear  = years[0]?.year_year || '';
  const lastYear   = years[years.length - 1]?.year_year || '';

  return (
    <div className="a4-page">
      <div className="a4-content" ref={ref} style={{ fontSize: '15pt', display: 'flex', flexDirection: 'column', minHeight: '257mm' }}>
        {/* top-right label */}
        <div style={{ textAlign: 'right', fontSize: '15pt', fontWeight: 'bold', marginBottom: 6 }}>แบบ ปพ 6</div>

        {/* school header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img src={logoSrc} alt="logo" style={{ width: 110, height: 110, marginBottom: 8 }} /><br />
          <div style={{ fontSize: '18pt', fontWeight: 'bold', marginBottom: 4 }}>แบบรายงานผลการพัฒนาคุณภาพผู้เรียนรายบุคคล</div>
          <div style={{ fontSize: '16pt', marginBottom: 4 }}>{levelLabel}</div>
          <div style={{ fontSize: '17pt', fontWeight: 'bold', marginBottom: 4 }}>{school_name}</div>
          <div style={{ fontSize: '15pt' }}>{school_address}</div>
        </div>

        {/* student header info */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '15pt' }}>
              <tbody>
                <tr style={{ height: 36 }}>
                  <td style={{ border: 'none', whiteSpace: 'nowrap', fontWeight: 'bold', paddingRight: 8 }}>ชื่อ-สกุล</td>
                  <td style={{ border: 'none', borderBottom: '1px solid #000' }}>{student?.st_name}</td>
                  <td style={{ border: 'none', whiteSpace: 'nowrap', fontWeight: 'bold', paddingLeft: 16 }}>เลขประจำตัว</td>
                  <td style={{ border: 'none', borderBottom: '1px solid #000', minWidth: 90 }}>{student?.st_idnumber}</td>
                </tr>
                <tr style={{ height: 36 }}>
                  <td style={{ border: 'none', fontWeight: 'bold', whiteSpace: 'nowrap', paddingRight: 8 }}>หมายเลขประชาชน</td>
                  <td style={{ border: 'none', borderBottom: '1px solid #000' }}>{student?.st_idcard}</td>
                  <td style={{ border: 'none', fontWeight: 'bold', paddingLeft: 16, whiteSpace: 'nowrap' }}>วันเกิด</td>
                  <td style={{ border: 'none', borderBottom: '1px solid #000' }}>{toBEDate(student?.st_birthdate)}</td>
                </tr>
                <tr style={{ height: 36 }}>
                  <td style={{ border: 'none', fontWeight: 'bold', whiteSpace: 'nowrap', paddingRight: 8 }}>ปีการศึกษาที่เริ่มเรียน</td>
                  <td style={{ border: 'none', borderBottom: '1px solid #000' }}>{firstYear}</td>
                  <td style={{ border: 'none', fontWeight: 'bold', paddingLeft: 16, whiteSpace: 'nowrap' }}>ปีการศึกษาที่จบช่วงชั้น</td>
                  <td style={{ border: 'none', borderBottom: '1px solid #000' }}>{lastYear}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {/* Photo box */}
          <div style={{ border: '1px solid #000', width: 100, minHeight: 130, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12pt', textAlign: 'center', flexShrink: 0 }}>
            3×4 ซม.
          </div>
        </div>

        {/* year table */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24, fontSize: '15pt' }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #000', textAlign: 'center', padding: '8px 10px' }}>ปีการศึกษา</th>
              <th style={{ border: '1px solid #000', textAlign: 'center', padding: '8px 10px' }}>ภาคเรียน</th>
              <th style={{ border: '1px solid #000', textAlign: 'center', padding: '8px 10px' }}>ครูประจำชั้น</th>
              <th style={{ border: '1px solid #000', textAlign: 'center', padding: '8px 10px', width: 90 }}>ชั้น</th>
              <th style={{ border: '1px solid #000', textAlign: 'center', padding: '8px 10px', width: 80 }}>เลขที่</th>
            </tr>
          </thead>
          <tbody>
            {years.map((yr) => (
              <React.Fragment key={yr.year_id}>
                <tr>
                  <td rowSpan={2} style={{ border: '1px solid #000', textAlign: 'center', verticalAlign: 'middle', padding: '6px 10px' }}>{yr.year_year}</td>
                  <td style={{ border: '1px solid #000', textAlign: 'center', padding: '6px 10px' }}>1</td>
                  <td style={{ border: '1px solid #000', padding: '6px 10px' }}>{yr.advisor_name}</td>
                  <td style={{ border: '1px solid #000', textAlign: 'center', padding: '6px' }}>ม.{yr.c_id}/{yr.r_no}</td>
                  <td style={{ border: '1px solid #000', textAlign: 'center' }}>{roomStudentNumbers?.[yr.r_id] || ''}</td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid #000', textAlign: 'center', padding: '6px 10px' }}>2</td>
                  <td style={{ border: '1px solid #000', padding: '6px 10px' }}>{yr.advisor_name}</td>
                  <td style={{ border: '1px solid #000', textAlign: 'center', padding: '6px' }}>ม.{yr.c_id}/{yr.r_no}</td>
                  <td style={{ border: '1px solid #000', textAlign: 'center' }}>{roomStudentNumbers?.[yr.r_id] || ''}</td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {/* signature */}
        <div style={{ marginTop: 'auto', paddingTop: 40 }}>
          <div style={{ textAlign: 'center', fontSize: '15pt' }}>
            <div style={{ marginBottom: 6 }}>ลงชื่อ ................................................ ผู้อำนวยการ</div>
            <div>( {principal_name} )</div>
          </div>
          <div style={{ textAlign: 'center', marginTop: 24, fontSize: '14pt' }}>{school_office}</div>
        </div>
      </div>
      <div className="print-button-container no-print">
        <Button variant={section === 'junior' ? 'info' : 'primary'} className="print-btn" onClick={() => printNode(ref.current)}>
          <i className="ri-printer-line me-1"></i> ปริ้นหน้าปก ({section === 'junior' ? 'ม.1-3' : 'ม.4-6'})
        </Button>
      </div>
    </div>
  );
}

function PersonalPage({ student }) {
  const ref = useRef();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (student) setForm({ ...student }); }, [student]);

  const sf = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const inStyle = { width: '100%', border: 'none', outline: 'none', background: 'transparent', fontSize: 'inherit', padding: 0 };

  const ci = (field) => (
    <>
      <span className="no-print"><input type="text" value={form[field] ?? ''} onChange={e => sf(field, e.target.value)} style={inStyle} /></span>
      <span className="print-only">{form[field] ?? ''}</span>
    </>
  );

  const cs = (field, options) => (
    <>
      <span className="no-print">
        <select value={form[field] ?? ''} onChange={e => sf(field, e.target.value)}
          style={{ ...inStyle, cursor: 'pointer' }}>
          <option value="">-</option>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </span>
      <span className="print-only">{options.find(o => o.value === form[field])?.label ?? form[field] ?? ''}</span>
    </>
  );

  const handleSave = () => {
    setSaving(true);
    axiosInstance.post(`${process.env.REACT_APP_API}/updateStudentInfo`, form,
      { headers: { Authorization: `Bearer ${getToken()}` } }
    ).then(() => Swal.fire({ title: 'บันทึกสำเร็จ', icon: 'success', timer: 1200, showConfirmButton: false }))
     .catch(() => Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: 'ไม่สามารถบันทึกได้' }))
     .finally(() => setSaving(false));
  };

  const ciIdCard = () => {
    const val = form.st_idcard ?? '';
    const invalid = val.length > 0 && (val.length !== 13 || !/^\d{13}$/.test(val));
    return (
      <>
        <span className="no-print">
          <input type="text" value={val} maxLength={13}
            onChange={e => sf('st_idcard', e.target.value.replace(/\D/g, '').slice(0, 13))}
            style={{ ...inStyle, color: invalid ? '#dc3545' : undefined, outline: invalid ? '1px solid #dc3545' : 'none' }}
            placeholder="0000000000000" />
          {invalid && <span style={{ color: '#dc3545', fontSize: '9pt', marginLeft: 4 }}>ต้องมี 13 หลัก</span>}
        </span>
        <span className="print-only">{val}</span>
      </>
    );
  };

  const ciDate = (field) => {
    const val = form[field] ?? '';
    const isValidDate = (s) => /^\d{2}\/\d{2}\/\d{4}$/.test(s);
    const invalid = val.length > 0 && !isValidDate(val);
    const handleDateChange = (raw) => {
      const digits = raw.replace(/\D/g, '').slice(0, 8);
      let formatted = digits;
      if (digits.length > 4) formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
      else if (digits.length > 2) formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
      sf(field, formatted);
    };
    return (
      <>
        <span className="no-print">
          <input type="text" value={val} maxLength={10}
            onChange={e => handleDateChange(e.target.value)}
            style={{ ...inStyle, color: invalid ? '#dc3545' : undefined, outline: invalid ? '1px solid #dc3545' : 'none' }}
            placeholder="วว/ดด/ปปปป" />
        </span>
        <span className="print-only">{val}</span>
      </>
    );
  };

  const GENDER_OPT = [{ value: 'm', label: 'ชาย' }, { value: 'f', label: 'หญิง' }];
  const PARENT_OPT = [{ value: 'dad', label: 'บิดา' }, { value: 'mom', label: 'มารดา' }, { value: 'other', label: 'บุคคลอื่น' }];

  const extraNote = form.st_extraNode || '';

  return (
    <div className="a4-page">
      <div className="a4-content" ref={ref}>
        <div style={{ textAlign: 'right', fontWeight: 'bold', marginBottom: 4 }}>แบบ ปพ 6</div>
        <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '16pt', marginBottom: 10, textDecoration: 'underline' }}>ข้อมูลส่วนตัวผู้เรียน</div>

        <table className="form-table" style={{ marginBottom: 8 }}>
          <tbody>
            <tr>
              <td className="form-label">ชื่อ - สกุล</td>
              <td colSpan={3}>{ci('st_name')}</td>
              <td className="form-label">เพศ</td>
              <td>{cs('st_gen', GENDER_OPT)}</td>
              <td className="form-label">หมู่โลหิต</td>
              <td>{ci('st_blood')}</td>
            </tr>
            <tr>
              <td className="form-label">เลขประจำตัว</td>
              <td style={{ background: '#f5f5f5' }}>{form.st_idnumber ?? ''}</td>
              <td className="form-label">หมายเลขประชาชน</td>
              <td colSpan={3}>{ciIdCard()}</td>
              <td className="form-label">วันเกิด</td>
              <td>{ciDate('st_birthdate')}</td>
            </tr>
            <tr>
              <td className="form-label">ความสามารถพิเศษ</td>
              <td colSpan={7}>{ci('st_favoritesport')}</td>
            </tr>
            <tr>
              <td className="form-label">โรคประจำตัว</td>
              <td colSpan={7}>{ci('st_syndrome')}</td>
            </tr>
            <tr>
              <td className="form-label">วิธีแก้ไขในเบื้องต้น</td>
              <td colSpan={7}>{ci('st_drug')}</td>
            </tr>
            <tr>
              <td className="form-label">ชื่อบิดา</td>
              <td colSpan={3}>{ci('st_fatherName')}</td>
              <td className="form-label">อาชีพ</td>
              <td>{ci('st_fatherJob')}</td>
              <td className="form-label" style={{ width: '7%', whiteSpace: 'nowrap' }}>โทรศัพท์</td>
              <td>{ci('st_fatherPhone')}</td>
            </tr>
            <tr>
              <td className="form-label">ชื่อมารดา</td>
              <td colSpan={3}>{ci('st_motherName')}</td>
              <td className="form-label">อาชีพ</td>
              <td>{ci('st_motherJob')}</td>
              <td className="form-label" style={{ width: '7%', whiteSpace: 'nowrap' }}>โทรศัพท์</td>
              <td>{ci('st_motherPhone')}</td>
            </tr>
            <tr>
              <td className="form-label">ชื่อผู้ปกครอง</td>
              <td colSpan={3}>{ci('st_parentname')}</td>
              <td className="form-label">อาชีพ</td>
              <td>{ci('st_parentjob')}</td>
              <td className="form-label" style={{ width: '7%', whiteSpace: 'nowrap' }}>โทรศัพท์</td>
              <td>{ci('st_parentPhone')}</td>
            </tr>
            <tr>
              <td className="form-label">ผู้ปกครองกับนักเรียนเกี่ยวข้องเป็น</td>
              <td colSpan={7}>{cs('st_parent', PARENT_OPT)}</td>
            </tr>
            <tr>
              <td className="form-label">ที่อยู่นักเรียน</td>
              <td colSpan={7}>
                <span className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'nowrap' }}>
                  <input value={form.st_current ?? ''} onChange={e => sf('st_current', e.target.value)} style={{ ...inStyle, flex: 2, minWidth: 80 }} />
                  <span style={{ whiteSpace: 'nowrap' }}>ตำบล</span>
                  <input value={form.st_currentSubDistrict ?? ''} onChange={e => sf('st_currentSubDistrict', e.target.value)} style={{ ...inStyle, flex: 1, minWidth: 60 }} />
                  <span style={{ whiteSpace: 'nowrap' }}>อำเภอ</span>
                  <input value={form.st_currentDistrict ?? ''} onChange={e => sf('st_currentDistrict', e.target.value)} style={{ ...inStyle, flex: 1, minWidth: 60 }} />
                  <span style={{ whiteSpace: 'nowrap' }}>จังหวัด</span>
                  <input value={form.st_currentProvince ?? ''} onChange={e => sf('st_currentProvince', e.target.value)} style={{ ...inStyle, flex: 1, minWidth: 60 }} />
                  <span style={{ whiteSpace: 'nowrap' }}>รหัสไปรษณีย์</span>
                  <input value={form.st_zipcode ?? ''} onChange={e => sf('st_zipcode', e.target.value)} style={{ ...inStyle, flex: 0.8, minWidth: 50 }} />
                </span>
                <span className="print-only">
                  {form.st_current ?? ''}&nbsp;&nbsp;ตำบล&nbsp;&nbsp;{form.st_currentSubDistrict ?? ''}&nbsp;&nbsp;อำเภอ&nbsp;&nbsp;{form.st_currentDistrict ?? ''}&nbsp;&nbsp;จังหวัด&nbsp;&nbsp;{form.st_currentProvince ?? ''}&nbsp;&nbsp;รหัสไปรษณีย์&nbsp;&nbsp;{form.st_zipcode ?? ''}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <div className="no-print" style={{ textAlign: 'right', marginBottom: 6 }}>
          <Button variant="success" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : <><i className="ri-save-line me-1"></i>บันทึกข้อมูลส่วนตัว</>}
          </Button>
        </div>

        <div style={{ fontWeight: 'bold', marginBottom: 4, borderBottom: '1px solid #000' }}>บันทึกเพิ่มเติม</div>
        <div style={{ border: '1px solid #000', padding: '4px 4px 2px' }}>
          <span className="no-print" style={{ display: 'block' }}>
            <textarea value={extraNote} onChange={e => sf('st_extraNode', e.target.value)} rows={12}
              style={{
                width: '100%', border: 'none', outline: 'none', resize: 'none',
                fontSize: 'inherit', fontFamily: 'inherit', background: 'transparent',
                padding: '2px 4px', lineHeight: '24px',
                backgroundImage: 'repeating-linear-gradient(transparent, transparent 23px, #bbb 23px, #bbb 24px)',
                backgroundAttachment: 'local',
              }} />
          </span>
          <span className="print-only">
            {(() => {
              const lines = extraNote ? extraNote.split('\n') : [];
              const blank = Math.max(0, 12 - lines.length);
              return <>
                {lines.map((ln, i) => (
                  <div key={i} style={{ borderBottom: '1px dotted #aaa', height: 24, lineHeight: '24px', paddingLeft: 4, marginBottom: 2 }}>{ln || ' '}</div>
                ))}
                {Array.from({ length: blank }).map((_, i) => (
                  <div key={`b${i}`} style={{ borderBottom: '1px dotted #aaa', height: 24, marginBottom: 2 }} />
                ))}
              </>;
            })()}
          </span>
        </div>
        <div className="no-print" style={{ textAlign: 'right', marginTop: 4 }}>
          <Button variant="success" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : <><i className="ri-save-line me-1"></i>บันทึกเพิ่มเติม</>}
          </Button>
        </div>
      </div>
      <div className="print-button-container no-print">
        <Button variant="primary" className="print-btn" onClick={() => printNode(ref.current)}>
          <i className="ri-printer-line me-1"></i> ปริ้นหน้าข้อมูลส่วนตัว
        </Button>
      </div>
    </div>
  );
}

function GradePage({ student, yearData }) {
  const ref = useRef();
  const { school_name, principal_name, logoSrc } = useContext(SchoolCtx);
  const sem1 = yearData.semesters.find(s => s.sub_semester === '1') || { grades: [] };
  const sem2 = yearData.semesters.find(s => s.sub_semester === '2') || { grades: [] };
  const isJunior = Number(yearData.c_id) <= 3;
  const activities = isJunior ? ACTIVITY_DEFAULT : ACTIVITY_DEFAULT.filter(a => a.name !== 'กิจกรรมลูกเสือ เนตรนารี');

  const toItemArray = (d) => DESIRABLE.map((_, i) => d?.[`dr_item${i + 1}`] ?? '');
  const [dr1, setDr1] = useState(() => toItemArray(yearData.desirable?.['1']));
  const [dr2, setDr2] = useState(() => toItemArray(yearData.desirable?.['2']));
  const [savingDr, setSavingDr] = useState(false);

  const handleSaveDesirable = () => {
    if (!yearData.study_id) return;
    setSavingDr(true);
    const toNullable = (arr) => arr.map(v => v === '' ? null : Number(v));
    Promise.all([
      axiosInstance.post(`${process.env.REACT_APP_API}/saveDesirableSingle`,
        { study_id: yearData.study_id, semester: '1', items: toNullable(dr1), update_by: getUserId() },
        { headers: { Authorization: `Bearer ${getToken()}` } }),
      axiosInstance.post(`${process.env.REACT_APP_API}/saveDesirableSingle`,
        { study_id: yearData.study_id, semester: '2', items: toNullable(dr2), update_by: getUserId() },
        { headers: { Authorization: `Bearer ${getToken()}` } }),
    ]).then(() => {
      Swal.fire({ title: 'บันทึกสำเร็จ', icon: 'success', timer: 1200, showConfirmButton: false });
    }).catch(() => {
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: 'ไม่สามารถบันทึกได้' });
    }).finally(() => setSavingDr(false));
  };

  // Pad to same length for side-by-side display
  const maxRows = Math.max(sem1.grades.length, sem2.grades.length, 10);
  const rows = Array.from({ length: maxRows });

  const passed = (g) => g.g_grade !== null && g.g_grade !== undefined && Number(g.g_grade) >= 50;

  const hasScore = (g) => g.g_grade !== null && g.g_grade !== undefined && !isNaN(Number(g.g_grade));

  const semSummary = (sem) => {
    const basic   = sem.grades.filter(g => g.sub_type === 'basic');
    const extra   = sem.grades.filter(g => g.sub_type === 'extra');
    const numeric = sem.grades.filter(hasScore);
    const weightedSum   = numeric.reduce((s, g) => s + (g.sub_credit || 0) * scoreToGrade(g.g_grade), 0);
    const gradedCredits = numeric.reduce((s, g) => s + (g.sub_credit || 0), 0);
    return {
      basicCredit:  basic.reduce((s, g) => s + (g.sub_credit || 0), 0),
      extraCredit:  extra.reduce((s, g) => s + (g.sub_credit || 0), 0),
      basicEarned:  basic.filter(passed).reduce((s, g) => s + (g.sub_credit || 0), 0),
      extraEarned:  extra.filter(passed).reduce((s, g) => s + (g.sub_credit || 0), 0),
      weightedSum,
      gradedCredits,
    };
  };

  const s1 = semSummary(sem1);
  const s2 = semSummary(sem2);
  const totalBasic = s1.basicCredit + s2.basicCredit;
  const totalExtra = s1.extraCredit + s2.extraCredit;

  const calcGPA = (weightedSum, gradedCredits) =>
    gradedCredits > 0 ? (weightedSum / gradedCredits).toFixed(2) : '';

  const yearWeightedSum   = s1.weightedSum + s2.weightedSum;
  const yearGradedCredits = s1.gradedCredits + s2.gradedCredits;

  return (
    <div className="a4-page">
      <div className="a4-content" ref={ref} style={{ fontSize: '12pt', padding: '10mm 12mm' }}>
        {/* Page header */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 4 }}>
          <tbody>
            <tr>
              <td rowSpan={3} style={{ border: 'none', width: 70, textAlign: 'center', verticalAlign: 'middle' }}>
                <img src={logoSrc} alt="logo" style={{ width: 60, height: 60 }} />
              </td>
              <td style={{ border: 'none', textAlign: 'center', fontWeight: 'bold', fontSize: '13pt' }}>รายงานการพัฒนาคุณภาพผู้เรียนเป็นรายบุคคล</td>
              <td style={{ border: 'none', textAlign: 'right', fontWeight: 'bold', verticalAlign: 'top' }}>ปพ.6</td>
            </tr>
            <tr>
              <td style={{ border: 'none', textAlign: 'center' }}>{school_name}</td>
              <td style={{ border: 'none' }}></td>
            </tr>
            <tr>
              <td colSpan={2} style={{ border: 'none' }}>
                <span style={{ fontWeight: 'bold' }}>ชื่อ - สกุล</span> {student?.st_name}&emsp;
                <span style={{ fontWeight: 'bold' }}>เลขประจำตัว</span> {student?.st_idnumber}&emsp;
                <span style={{ fontWeight: 'bold' }}>มัธยมศึกษาปีที่</span> {yearData.c_id}/{yearData.room?.r_no}&emsp;
                <span style={{ fontWeight: 'bold' }}>ปีการศึกษา</span> {yearData.year_year}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Grade table — two semesters side by side */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11pt' }}>
          <thead>
            <tr>
              <th colSpan={5} style={{ border: '1px solid #000', textAlign: 'center', background: '#f0f0f0' }}>ภาคเรียนที่ 1</th>
              <th colSpan={5} style={{ border: '1px solid #000', textAlign: 'center', background: '#f0f0f0' }}>ภาคเรียนที่ 2</th>
              <th rowSpan={2} style={{ border: '1px solid #000', textAlign: 'center', width: 50 }}>หมายเหตุ</th>
            </tr>
            <tr>
              <th style={{ border: '1px solid #000', textAlign: 'center', width: 24 }}>ที่</th>
              <th style={{ border: '1px solid #000' }}>รายวิชา</th>
              <th style={{ border: '1px solid #000', textAlign: 'center', width: 60 }}>รหัสวิชา</th>
              <th style={{ border: '1px solid #000', textAlign: 'center', width: 30 }}>นก.</th>
              <th style={{ border: '1px solid #000', textAlign: 'center', width: 36 }}>เกรด</th>
              <th style={{ border: '1px solid #000', textAlign: 'center', width: 24 }}>ที่</th>
              <th style={{ border: '1px solid #000' }}>รายวิชา</th>
              <th style={{ border: '1px solid #000', textAlign: 'center', width: 60 }}>รหัสวิชา</th>
              <th style={{ border: '1px solid #000', textAlign: 'center', width: 30 }}>นก.</th>
              <th style={{ border: '1px solid #000', textAlign: 'center', width: 36 }}>เกรด</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((_, i) => {
              const g1 = sem1.grades[i];
              const g2 = sem2.grades[i];
              return (
                <tr key={i} style={{ height: 20 }}>
                  <td style={{ border: '1px solid #000', textAlign: 'center', fontSize: '10pt' }}>{g1 ? i + 1 : ''}</td>
                  <td style={{ border: '1px solid #000', fontSize: '10pt', padding: '1px 3px' }}>{g1?.sub_subname}</td>
                  <td style={{ border: '1px solid #000', textAlign: 'center', fontSize: '10pt' }}>{g1?.sub_subid}</td>
                  <td style={{ border: '1px solid #000', textAlign: 'center', fontSize: '10pt' }}>{g1?.sub_credit}</td>
                  <td style={{ border: '1px solid #000', textAlign: 'center', fontSize: '10pt' }}>{g1 && g1.g_grade !== null && g1.g_grade !== undefined ? scoreToGrade(g1.g_grade) : ''}</td>
                  <td style={{ border: '1px solid #000', textAlign: 'center', fontSize: '10pt' }}>{g2 ? i + 1 : ''}</td>
                  <td style={{ border: '1px solid #000', fontSize: '10pt', padding: '1px 3px' }}>{g2?.sub_subname}</td>
                  <td style={{ border: '1px solid #000', textAlign: 'center', fontSize: '10pt' }}>{g2?.sub_subid}</td>
                  <td style={{ border: '1px solid #000', textAlign: 'center', fontSize: '10pt' }}>{g2?.sub_credit}</td>
                  <td style={{ border: '1px solid #000', textAlign: 'center', fontSize: '10pt' }}>{g2 && g2.g_grade !== null && g2.g_grade !== undefined ? scoreToGrade(g2.g_grade) : ''}</td>
                  <td style={{ border: '1px solid #000' }}></td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* กิจกรรมพัฒนาผู้เรียน */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10pt', marginTop: 4 }}>
          <thead>
            <tr>
              <th colSpan={4} style={{ border: '1px solid #000', textAlign: 'center', background: '#f0f0f0' }}>กิจกรรมพัฒนาผู้เรียน ภาคเรียนที่ 1</th>
              <th colSpan={4} style={{ border: '1px solid #000', textAlign: 'center', background: '#f0f0f0' }}>กิจกรรมพัฒนาผู้เรียน ภาคเรียนที่ 2</th>
              <th style={{ border: '1px solid #000', textAlign: 'center' }}>หมายเหตุ</th>
            </tr>
            <tr>
              <th style={{ border: '1px solid #000' }}>กิจกรรม</th>
              <th style={{ border: '1px solid #000', width: 55 }}>รหัส</th>
              <th style={{ border: '1px solid #000', width: 36 }}>ชม.</th>
              <th style={{ border: '1px solid #000', width: 46 }}>ประเมิน</th>
              <th style={{ border: '1px solid #000' }}>กิจกรรม</th>
              <th style={{ border: '1px solid #000', width: 55 }}>รหัส</th>
              <th style={{ border: '1px solid #000', width: 36 }}>ชม.</th>
              <th style={{ border: '1px solid #000', width: 46 }}>ประเมิน</th>
              <th style={{ border: '1px solid #000' }}></th>
            </tr>
          </thead>
          <tbody>
            {activities.map((act, i) => {
              const code2 = act.code.replace(/(\d+)$/, (m) => String(Number(m) + 4));
              return (
                <tr key={i} style={{ height: 20 }}>
                  <td style={{ border: '1px solid #000', padding: '1px 3px' }}>{act.name}</td>
                  <td style={{ border: '1px solid #000', textAlign: 'center' }}>{act.code}</td>
                  <td style={{ border: '1px solid #000', textAlign: 'center' }}>{act.credit}</td>
                  <td style={{ border: '1px solid #000', textAlign: 'center' }}>ผ่าน</td>
                  <td style={{ border: '1px solid #000', padding: '1px 3px' }}>{act.name}</td>
                  <td style={{ border: '1px solid #000', textAlign: 'center' }}>{code2}</td>
                  <td style={{ border: '1px solid #000', textAlign: 'center' }}>{act.credit}</td>
                  <td style={{ border: '1px solid #000', textAlign: 'center' }}>ผ่าน</td>
                  <td style={{ border: '1px solid #000' }}></td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Bottom two sections side by side */}
        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
          {/* คุณลักษณะ */}
          <div style={{ flex: 1 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10pt' }}>
              <thead>
                <tr>
                  <th style={{ border: '1px solid #000', textAlign: 'center' }}>คุณลักษณะอันพึงประสงค์</th>
                  <th style={{ border: '1px solid #000', textAlign: 'center', width: 64 }}>ภาคเรียน 1</th>
                  <th style={{ border: '1px solid #000', textAlign: 'center', width: 64 }}>ภาคเรียน 2</th>
                </tr>
              </thead>
              <tbody>
                {DESIRABLE.map((item, i) => {
                  const drSelect = (val, setter) => (
                    <>
                      <span className="no-print">
                        <select value={val ?? ''} onChange={e => setter(prev => { const n=[...prev]; n[i]=e.target.value; return n; })}
                          style={{ width: '100%', fontSize: '9pt', border: 'none', background: 'transparent', outline: 'none' }}>
                          <option value="">-</option>
                          {Object.entries(SCORE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                      </span>
                      <span className="print-only">{val !== '' && val != null ? SCORE_LABELS[val] : ''}</span>
                    </>
                  );
                  return (
                    <tr key={i} style={{ height: 22 }}>
                      <td style={{ border: '1px solid #000', padding: '1px 3px' }}>{i + 1}. {item}</td>
                      <td className="text-center" style={{ border: '1px solid #000', textAlign: 'center', padding: '1px 2px' }}>{drSelect(dr1[i], setDr1)}</td>
                      <td className="text-center" style={{ border: '1px solid #000', textAlign: 'center', padding: '1px 2px' }}>{drSelect(dr2[i], setDr2)}</td>
                    </tr>
                  );
                })}
                <tr style={{ height: 20 }}>
                  <td style={{ border: '1px solid #000', fontWeight: 'bold', padding: '1px 3px' }}>สรุปผลการประเมิน</td>
                  <td style={{ border: '1px solid #000', textAlign: 'center', fontWeight: 'bold' }}>
                    {desirableSummary(Object.fromEntries(dr1.map((v,i) => [`dr_item${i+1}`, v === '' ? null : Number(v)])))}
                  </td>
                  <td style={{ border: '1px solid #000', textAlign: 'center', fontWeight: 'bold' }}>
                    {desirableSummary(Object.fromEntries(dr2.map((v,i) => [`dr_item${i+1}`, v === '' ? null : Number(v)])))}
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="no-print" style={{ textAlign: 'right', marginTop: 4 }}>
              <Button variant="success" size="sm" onClick={handleSaveDesirable} disabled={savingDr || !yearData.study_id}>
                {savingDr ? <Spinner animation="border" size="sm" /> : <><i className="ri-save-line me-1"></i>บันทึกคุณลักษณะ</>}
              </Button>
            </div>
          </div>

          {/* สรุปผลการเรียน */}
          <div style={{ flex: 1 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10pt' }}>
              <thead>
                <tr>
                  <th rowSpan={2} style={{ border: '1px solid #000', textAlign: 'center' }}>ภาคเรียน</th>
                  <th colSpan={2} style={{ border: '1px solid #000', textAlign: 'center' }}>พื้นฐาน</th>
                  <th colSpan={2} style={{ border: '1px solid #000', textAlign: 'center' }}>เพิ่มเติม</th>
                  <th rowSpan={2} style={{ border: '1px solid #000', textAlign: 'center', width: 40 }}>เฉลี่ย</th>
                </tr>
                <tr>
                  <th style={{ border: '1px solid #000', textAlign: 'center', width: 32 }}>เรียน</th>
                  <th style={{ border: '1px solid #000', textAlign: 'center', width: 32 }}>ได้</th>
                  <th style={{ border: '1px solid #000', textAlign: 'center', width: 32 }}>เรียน</th>
                  <th style={{ border: '1px solid #000', textAlign: 'center', width: 32 }}>ได้</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'ภาคเรียนที่ 1', bs: s1.basicCredit, be: s1.basicEarned, es: s1.extraCredit, ee: s1.extraEarned, gpa: calcGPA(s1.weightedSum, s1.gradedCredits) },
                  { label: 'ภาคเรียนที่ 2', bs: s2.basicCredit, be: s2.basicEarned, es: s2.extraCredit, ee: s2.extraEarned, gpa: calcGPA(s2.weightedSum, s2.gradedCredits) },
                  { label: 'ตลอดปี',        bs: totalBasic, be: s1.basicEarned + s2.basicEarned, es: totalExtra, ee: s1.extraEarned + s2.extraEarned, gpa: calcGPA(yearWeightedSum, yearGradedCredits) }
                ].map(({ label, bs, be, es, ee, gpa }) => (
                  <tr key={label} style={{ height: 20 }}>
                    <td style={{ border: '1px solid #000', padding: '1px 3px' }}>{label}</td>
                    <td style={{ border: '1px solid #000', textAlign: 'center' }}>{bs || ''}</td>
                    <td style={{ border: '1px solid #000', textAlign: 'center' }}>{be || ''}</td>
                    <td style={{ border: '1px solid #000', textAlign: 'center' }}>{es || ''}</td>
                    <td style={{ border: '1px solid #000', textAlign: 'center' }}>{ee || ''}</td>
                    <td style={{ border: '1px solid #000', textAlign: 'center' }}>{gpa}</td>
                  </tr>
                ))}
                <tr style={{ height: 20 }}>
                  <td colSpan={4} style={{ border: '1px solid #000', padding: '1px 3px', fontSize: '9pt', fontWeight: 'bold', background: '#f0f0f0' }}>ผลการประเมิน</td>
                  <td colSpan={1} style={{ border: '1px solid #000', textAlign: 'center', fontSize: '9pt', fontWeight: 'bold', background: '#f0f0f0' }}>กิจกรรม</td>
                  <td colSpan={1} style={{ border: '1px solid #000', textAlign: 'center', fontSize: '9pt', fontWeight: 'bold', background: '#f0f0f0' }}>การอ่าน</td>
                </tr>
                <tr style={{ height: 20 }}>
                  <td colSpan={4} style={{ border: '1px solid #000', padding: '1px 3px', fontSize: '9pt' }}>ภาคเรียนที่ 1</td>
                  <td className="text-center" colSpan={1} style={{ border: '1px solid #000' }}>ผ่าน</td>
                  <td colSpan={1} style={{ border: '1px solid #000', textAlign: 'center' }}>{gpaLabel(calcGPA(s1.weightedSum, s1.gradedCredits))}</td>
                </tr>
                <tr style={{ height: 20 }}>
                  <td colSpan={4} style={{ border: '1px solid #000', padding: '1px 3px', fontSize: '9pt' }}>ภาคเรียนที่ 2</td>
                  <td className="text-center" colSpan={1} style={{ border: '1px solid #000' }}>ผ่าน</td>
                  <td colSpan={1} style={{ border: '1px solid #000', textAlign: 'center' }}>{gpaLabel(calcGPA(s2.weightedSum, s2.gradedCredits))}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Signature */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14, fontSize: '11pt' }}>
          <div style={{ textAlign: 'center' }}>
            <div>ลงชื่อ ........................................ ครูประจำชั้น</div>
            <div>( {yearData.advisor_name || '..........................................'} )</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div>ลงชื่อ ........................................ ผู้อำนวยการ</div>
            <div>( {principal_name} )</div>
          </div>
        </div>
      </div>
      <div className="print-button-container no-print">
        <Button variant={isJunior ? 'info' : 'primary'} className="print-btn" onClick={() => printNode(ref.current)}>
          <i className="ri-printer-line me-1"></i> ปริ้นผลการเรียน ปีการศึกษา {yearData.year_year}
        </Button>
      </div>
    </div>
  );
}

function OpinionPage({ student, yearData }) {
  const ref = useRef();
  const { school_name, logoSrc } = useContext(SchoolCtx);
  const isJunior = Number(yearData.c_id) <= 3;

  const EMPTY = Array(OPINION_ITEMS.length).fill('');
  const [advisorOp, setAdvisorOp] = useState(EMPTY);
  const [parentOp,  setParentOp]  = useState(EMPTY);
  const [loadedAdvisor, setLoadedAdvisor] = useState(false);
  const [loadedParent,  setLoadedParent]  = useState(false);
  const [savingAdvisor, setSavingAdvisor] = useState(false);
  const [savingParent,  setSavingParent]  = useState(false);

  const loadOpinion = (type, setter, setLoaded) => {
    axiosInstance.get(
      `${process.env.REACT_APP_API}/getTeacherOpinion/${student.st_id}/${yearData.year_id}/${type}`,
      { headers: { Authorization: `Bearer ${getToken()}` } }
    ).then(res => {
      const d = res.data;
      if (d && Object.keys(d).length > 0) {
        setter(OPINION_ITEMS.map((_, i) => d[`top_item${i + 1}`] || ''));
      }
      setLoaded(true);
    }).catch(() => setLoaded(true));
  };

  useEffect(() => {
    if (!student?.st_id || !yearData?.year_id) return;
    loadOpinion('advisor', setAdvisorOp, setLoadedAdvisor);
    loadOpinion('parent',  setParentOp,  setLoadedParent);
  }, [student?.st_id, yearData?.year_id]); // eslint-disable-line

  const handleSave = (type, items, setSaving) => {
    setSaving(true);
    axiosInstance.post(
      `${process.env.REACT_APP_API}/saveTeacherOpinion`,
      { st_id: student.st_id, year_id: yearData.year_id, type, items, update_by: getUserId() },
      { headers: { Authorization: `Bearer ${getToken()}` } }
    ).then(() => {
      Swal.fire({ title: 'บันทึกสำเร็จ', icon: 'success', timer: 1200, showConfirmButton: false });
    }).catch(() => {
      Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: 'ไม่สามารถบันทึกได้' });
    }).finally(() => setSaving(false));
  };

  const opinionInput = (values, setter, loaded) => (item, i) => (
    <tr key={i} style={{ height: 36 }}>
      <td style={{ border: '1px solid #000', padding: '2px 6px' }}>{i + 1}. {item}</td>
      <td style={{ border: '1px solid #000', padding: '2px 4px', textAlign: 'center' }}>
        <span className="no-print">
          {loaded
            ? <input type="text" value={values[i]}
                onChange={e => setter(prev => { const n = [...prev]; n[i] = e.target.value; return n; })}
                style={{ width: '100%', border: 'none', outline: 'none', fontSize: '11pt', background: 'transparent', textAlign: 'center' }} />
            : <Spinner animation="border" size="sm" />}
        </span>
        <span className="print-only">{values[i]}</span>
      </td>
    </tr>
  );

  return (
    <div className="a4-page">
      <div className="a4-content" ref={ref} style={{ fontSize: '12pt', padding: '10mm 12mm' }}>
        {/* header */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 6 }}>
          <tbody>
            <tr>
              <td rowSpan={3} style={{ border: 'none', width: 70, textAlign: 'center', verticalAlign: 'middle' }}>
                <img src={logoSrc} alt="logo" style={{ width: 60, height: 60 }} />
              </td>
              <td style={{ border: 'none', textAlign: 'center', fontWeight: 'bold', fontSize: '13pt' }}>รายงานการพัฒนาคุณภาพผู้เรียนเป็นรายบุคคล</td>
              <td style={{ border: 'none', textAlign: 'right', fontWeight: 'bold' }}>ปพ.6</td>
            </tr>
            <tr>
              <td style={{ border: 'none', textAlign: 'center' }}>{school_name}</td>
              <td style={{ border: 'none' }}></td>
            </tr>
            <tr>
              <td colSpan={2} style={{ border: 'none' }}>
                <span style={{ fontWeight: 'bold' }}>ชื่อ - สกุล</span> {student?.st_name}&emsp;
                <span style={{ fontWeight: 'bold' }}>เลขประจำตัว</span> {student?.st_idnumber}&emsp;
                <span style={{ fontWeight: 'bold' }}>มัธยมศึกษาปีที่</span> {yearData.c_id}/{yearData.room?.r_no}&emsp;
                <span style={{ fontWeight: 'bold' }}>ปีการศึกษา</span> {yearData.year_year}
              </td>
            </tr>
          </tbody>
        </table>

        <div style={{ fontWeight: 'bold', marginBottom: 6 }}>
          ความคิดเห็นของครูประจำชั้น / ผู้ปกครองนักเรียน
        </div>

        {/* Advisor opinion */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11pt', marginBottom: 4 }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #000', textAlign: 'center', width: 140 }}>หัวข้อ</th>
              <th style={{ border: '1px solid #000', textAlign: 'center' }}>ความคิดเห็นของครูประจำชั้น</th>
            </tr>
          </thead>
          <tbody>
            {OPINION_ITEMS.map(opinionInput(advisorOp, setAdvisorOp, loadedAdvisor))}
          </tbody>
        </table>
        <div className="no-print" style={{ textAlign: 'right', marginBottom: 8 }}>
          <Button variant="success" size="sm" onClick={() => handleSave('advisor', advisorOp, setSavingAdvisor)} disabled={savingAdvisor || !loadedAdvisor}>
            {savingAdvisor ? <Spinner animation="border" size="sm" /> : <><i className="ri-save-line me-1"></i>บันทึกความคิดเห็นครู</>}
          </Button>
        </div>

        <div style={{ marginBottom: 12, fontSize: '11pt' }}>
          <div style={{ textAlign: 'center' }}>
            <div>ลงชื่อ ........................................ ครูประจำชั้น</div>
            <div>( {yearData.advisor_name || '..........................................'} )</div>
          </div>
        </div>

        {/* Parent opinion */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11pt', marginBottom: 4 }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #000', textAlign: 'center', width: 140 }}>หัวข้อ</th>
              <th style={{ border: '1px solid #000', textAlign: 'center' }}>ความคิดเห็นของผู้ปกครอง</th>
            </tr>
          </thead>
          <tbody>
            {OPINION_ITEMS.map(opinionInput(parentOp, setParentOp, loadedParent))}
          </tbody>
        </table>
        <div className="no-print" style={{ textAlign: 'right', marginBottom: 8 }}>
          <Button variant="warning" size="sm" onClick={() => handleSave('parent', parentOp, setSavingParent)} disabled={savingParent || !loadedParent}>
            {savingParent ? <Spinner animation="border" size="sm" /> : <><i className="ri-save-line me-1"></i>บันทึกความคิดเห็นผู้ปกครอง</>}
          </Button>
        </div>

        <div style={{ textAlign: 'center', fontSize: '11pt' }}>
          <div>ลงชื่อ ........................................ ผู้ปกครอง</div>
          <div>( ..........................................  )</div>
        </div>
      </div>
      <div className="print-button-container no-print">
        <Button variant={isJunior ? 'info' : 'primary'} className="print-btn" onClick={() => printNode(ref.current)}>
          <i className="ri-printer-line me-1"></i> ปริ้นหน้าความคิดเห็น ปีการศึกษา {yearData.year_year}
        </Button>
      </div>
    </div>
  );
}

// ——— Main component ———
export default function StudentPP6() {
  const { studentID } = useParams();
  const { setLoading } = useLoading();
  const schoolSettings = useSchoolSettings();
  const [studentData, setStudentData] = useState(null);
  const [ppData, setPPData] = useState(null);
  const [roomStudentNumbers, setRoomStudentNumbers] = useState({});

  useEffect(() => {
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/getStudentInfo/${studentID}`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    })
    .then(res => setStudentData(res.data))
    .catch(err => console.error(err))
    .finally(() => setLoading(false));
  }, [studentID, setLoading]);

  useEffect(() => {
    if (!studentData?.st_id) return;
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/getStudentGradePP/${studentData.st_id}`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    })
    .then(res => setPPData(res.data))
    .catch(err => console.error(err))
    .finally(() => setLoading(false));
  }, [studentData, setLoading]);

  useEffect(() => {
    const historyRows = ppData?.study_history || [];
    const roomIds = [...new Set(historyRows.map(row => row.r_id).filter(Boolean))];

    if (!studentData?.st_id || roomIds.length === 0) {
      setRoomStudentNumbers({});
      return;
    }

    let cancelled = false;

    Promise.all(
      roomIds.map(roomId =>
        axiosInstance.get(`${process.env.REACT_APP_API}/listStudentsInClass/${roomId}`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        })
        .then(res => {
          const index = (res.data || []).findIndex(st => String(st.st_id) === String(studentData.st_id));
          return [roomId, index >= 0 ? index + 1 : ''];
        })
        .catch(() => [roomId, ''])
      )
    ).then(entries => {
      if (!cancelled) setRoomStudentNumbers(Object.fromEntries(entries));
    });

    return () => { cancelled = true; };
  }, [ppData, studentData?.st_id]);

  if (!studentData) return null;

  const studyHistory  = ppData?.study_history  || [];
  const studyYears    = ppData?.study_years     || [];

  const hasJunior = studyHistory.some(r => [1, 2, 3].includes(Number(r.c_id)));
  const hasSenior = studyHistory.some(r => [4, 5, 6].includes(Number(r.c_id)));

  return (
    <SchoolCtx.Provider value={schoolSettings}>
      <Header />
      <div className="container mt-4">
        <div className="a4-wrapper">
          <div>
            {/* Cover pages */}
            {hasJunior && <CoverPage student={studentData} historyRows={studyHistory} section="junior" roomStudentNumbers={roomStudentNumbers} />}
            {hasSenior && <CoverPage student={studentData} historyRows={studyHistory} section="high" roomStudentNumbers={roomStudentNumbers} />}

            {/* Personal info */}
            <PersonalPage student={studentData} />

            {/* Per-year: grade + opinion */}
            {studyYears.map(yr => (
              <React.Fragment key={yr.year_id}>
                <GradePage   student={studentData} yearData={yr} />
                <OpinionPage student={studentData} yearData={yr} />
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </SchoolCtx.Provider>
  );
}
